import { updateAndSortFields } from './output-layout-utils';
import { buildOutputsFromProgramWizardModelForAscendQuest } from '../../../common';
import {
  doesSubcodeHaveAccess,
  ProgramConfigCategory,
  SSARProgram,
} from '@ssar-app/domain';
import { cloneDeep } from 'lodash';

export const prepareStandardOutputFields = async (
  data: SSARProgram,
  isCustomized: boolean,
  setPrevWizardData?: (data: SSARProgram) => void,
): Promise<SSARProgram> => {
  return await buildOutputsFromProgramWizardModelForAscendQuest(
    data?.subCode,
    data?.dataDictionaryVersion || '',
    data as SSARProgram,
  )
    .then((outputFields) => {
      const filteredFields = updateAndSortFields(
        outputFields.filter((a) =>
          doesSubcodeHaveAccess(data?.subCode, a.exclusiveSubCodes),
        ),
      );

      // initial clone copy of standard output layout
      const newData = { ...data };
      newData.outputLayout = {
        ...data.outputLayout,
        isCustomized: isCustomized,
        fields: filteredFields,
      };
      setPrevWizardData && setPrevWizardData(cloneDeep(newData as SSARProgram));
      return newData;
    })
    .catch((error) => {
      throw new Error(error);
    });
};

export const prepareCustomOutputFields = async (
  data: SSARProgram,
  setPrevWizardData?: (data: SSARProgram) => void,
): Promise<SSARProgram> => {
  const model = data.outputLayout;
  return await buildOutputsFromProgramWizardModelForAscendQuest(
    data?.subCode,
    data?.dataDictionaryVersion || '',
    data as SSARProgram,
    false,
  )
    .then((outputFieldsFromApi) => {
      const addedFields = outputFieldsFromApi.filter(
        (field) =>
          field.category !== ProgramConfigCategory.Custom &&
          field.category !== ProgramConfigCategory.ClientInput &&
          (model?.fields || []).findIndex(
            (mField) => mField.name === field.name,
          ) === -1,
      );
      const removedFields = (model?.fields || []).filter(
        (field) =>
          field.category !== ProgramConfigCategory.Custom &&
          field.category !== ProgramConfigCategory.ClientInput &&
          outputFieldsFromApi.findIndex(
            (mField) => mField.name === field.name,
          ) === -1,
      );
      let finalFields = (model?.fields || []).filter(
        (field) => !removedFields.find((ftr) => ftr.name === field.name),
      );
      finalFields = updateAndSortFields(finalFields.concat(addedFields));

      // initial clone copy of custom output layout
      const newData = { ...data };
      newData.outputLayout = {
        ...model,
        fields: finalFields,
      };
      setPrevWizardData && setPrevWizardData(cloneDeep(newData as SSARProgram));

      return newData;
    })
    .catch((error) => {
      throw new Error(error);
    });
};

export const loadLatestFields = async (
  tobeAdded: SSARProgram,
  tobeRemoved: SSARProgram,
  presentData: SSARProgram,
  setPrevWizardData?: (data: SSARProgram) => void,
): Promise<SSARProgram> => {
  return Promise.all([
    buildOutputsFromProgramWizardModelForAscendQuest(
      presentData?.subCode,
      presentData?.dataDictionaryVersion || '',
      tobeAdded,
      true,
    ),
    buildOutputsFromProgramWizardModelForAscendQuest(
      presentData?.subCode,
      presentData?.dataDictionaryVersion || '',
      tobeRemoved,
      true,
    ),
  ])
    .then(([fieldsTobeAdded, fieldsTobeRemoved]) => {
      const currentFields = presentData.outputLayout.fields;
      let finalFields = currentFields.filter(
        (field) => !fieldsTobeRemoved.find((ftr) => ftr.name === field.name),
      );
      const newAddFields = fieldsTobeAdded.filter(
        (field) =>
          finalFields.findIndex((fField) => field.name === fField.name) === -1,
      );
      finalFields = updateAndSortFields(finalFields.concat(newAddFields));

      // initial clone copy of custom output layout
      const newData = { ...presentData };
      newData.outputLayout = {
        ...presentData.outputLayout,
        fields: finalFields,
      };
      setPrevWizardData && setPrevWizardData(cloneDeep(newData as SSARProgram));
      return newData;
    })
    .catch((error) => {
      throw new Error(error);
    });
};
